/**
 * Enhanced Guests API Service with Retry and Offline Support
 * 
 * Updates the guests API service to use the standardized error handling system
 * and adds retry capabilities for improved resilience.
 */
import { AxiosInstance } from 'axios';
import { createApiClient, safeApiCall } from '@/services/api/client';
import { useError, ErrorType } from '@/shared/contexts/ErrorContext';
import { Guest, GuestInput } from '@/types/models';

/**
 * Guests API service with error handling and retry support
 */
export class GuestsService {
  private client: AxiosInstance;
  private addError: ReturnType<typeof useError>['addError'];

  constructor(client: AxiosInstance, addError: ReturnType<typeof useError>['addError']) {
    this.client = client;
    this.addError = addError;
  }

  /**
   * Get all guests
   * 
   * @returns {Promise<Guest[] | null>} List of guests or null on error
   */
  async getGuests(): Promise<Guest[] | null> {
    try {
      return await safeApiCall(
        async () => {
          const response = await this.client.get<{ items: Guest[] }>('/guests');
          return response.data.items || [];
        },
        this.addError
      );
    } catch (error) {
      this.addError({ 
        type: ErrorType.API,
        message: 'Failed to fetch guests' 
      });
      return null;
    }
  }

  /**
   * Get guest by ID
   * 
   * @param {string} id - Guest ID
   * @returns {Promise<Guest | null>} Guest details or null on error
   */
  async getGuestById(id: string): Promise<Guest | null> {
    try {
      return await safeApiCall(
        async () => {
          const response = await this.client.get<Guest>(`/guests/${id}`);
          return response.data;
        },
        this.addError
      );
    } catch (error) {
      this.addError({ 
        type: ErrorType.API,
        message: `Failed to fetch guest with ID ${id}` 
      });
      return null;
    }
  }

  /**
   * Create new guest
   * 
   * @param {GuestInput} guest - Guest data
   * @returns {Promise<Guest | null>} Created guest or null on error
   */
  async createGuest(guest: GuestInput): Promise<Guest | null> {
    try {
      return await safeApiCall(
        async () => {
          const response = await this.client.post<Guest>('/guests', guest);
          return response.data;
        },
        this.addError
      );
    } catch (error) {
      this.addError({ 
        type: ErrorType.API,
        message: 'Failed to create guest' 
      });
      return null;
    }
  }

  /**
   * Update existing guest
   * 
   * @param {string} id - Guest ID
   * @param {Partial<GuestInput>} guest - Guest data to update
   * @returns {Promise<Guest | null>} Updated guest or null on error
   */
  async updateGuest(id: string, guest: Partial<GuestInput>): Promise<Guest | null> {
    try {
      return await safeApiCall(
        async () => {
          const response = await this.client.put<Guest>(`/guests/${id}`, guest);
          return response.data;
        },
        this.addError
      );
    } catch (error) {
      this.addError({ 
        type: ErrorType.API,
        message: `Failed to update guest with ID ${id}` 
      });
      return null;
    }
  }

  /**
   * Delete guest
   * 
   * @param {string} id - Guest ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteGuest(id: string): Promise<boolean> {
    try {
      await safeApiCall(
        async () => {
          await this.client.delete(`/guests/${id}`);
        },
        this.addError
      );
      return true;
    } catch (error) {
      this.addError({ 
        type: ErrorType.API,
        message: `Failed to delete guest with ID ${id}` 
      });
      return false;
    }
  }
}

/**
 * Create guests service instance
 * 
 * @param {ReturnType<typeof useError>['addError']} addError - Error handler function
 * @returns {GuestsService} Guests service instance
 */
export const createGuestsService = (addError: ReturnType<typeof useError>['addError']): GuestsService => {
  const client = createApiClient(addError);
  return new GuestsService(client, addError);
};

export default {
  GuestsService,
  createGuestsService,
};
